import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import TagSelector from './TagSelector';
import './Trades.css';

function Trades({ username, onLogout, activeSubmenu, setActiveSubmenu }) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [accounts, setAccounts] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'trade_date', direction: 'desc' });
  const [tradeFilter, setTradeFilter] = useState('All'); // Default to 'All'
  const [dateRange, setDateRange] = useState('last_30_days');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('All'); // Long/Short/All
  const [optionTypeFilter, setOptionTypeFilter] = useState('All'); // Call/Put/All
  const [expandedSymbols, setExpandedSymbols] = useState(new Set());
  const [groupByConid, setGroupByConid] = useState(true); // Default to true, will be updated from settings
  const [expandedConids, setExpandedConids] = useState(new Set());
  const [pnlPeriod, setPnlPeriod] = useState('week'); // day/week/month/year
  const [expandedPnlGroups, setExpandedPnlGroups] = useState(new Set());

  useEffect(() => {
    fetchAccounts();
    fetchUserSettings();
  }, []);

  const fetchUserSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/user-settings/`);
      // Handle both list and single object responses
      let settings = null;
      if (response.data.results && Array.isArray(response.data.results)) {
        settings = response.data.results[0] || null;
      } else if (Array.isArray(response.data)) {
        settings = response.data[0] || null;
      } else {
        settings = response.data || null;
      }
      
      if (settings && settings.default_conid_grouped !== null && settings.default_conid_grouped !== undefined) {
        setGroupByConid(settings.default_conid_grouped);
      }
    } catch (err) {
      console.error('Error fetching user settings:', err);
      // Keep default value (true) if settings can't be fetched
    }
  };

  useEffect(() => {
    fetchTrades();
  }, [selectedAccount]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/accounts/?page_size=all`);
      const accountsData = response.data.results || response.data;
      setAccounts(Array.isArray(accountsData) ? accountsData : []);
    } catch (err) {
      setError('Error fetching accounts: ' + err.message);
      console.error(err);
    }
  };

  const fetchTrades = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `${API_URL}/portfolio/trades/?page_size=all`;
      if (selectedAccount && selectedAccount !== 'all') {
        const account = accounts.find(acc => 
          acc.id === parseInt(selectedAccount) || acc.broker_account_id === selectedAccount
        );
        if (account) {
          url += `&account_id=${account.broker_account_id}`;
        }
      }
      
      const response = await axios.get(url);
      const data = response.data.results || response.data;
      setTrades(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        setError('Session expired. Please log in again.');
      } else {
        setError('Error fetching trades: ' + err.message);
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectedAccountData = accounts.find(a =>
    a.id === parseInt(selectedAccount) || a.broker_account_id === selectedAccount
  );
  const currencySymbol = selectedAccountData?.broker === 'tastytrade' ? '$' : '€';

  const formatCurrency = (value) => {
    if (!value || value === '0.00') return `${currencySymbol} 0.00`;
    const num = parseFloat(value);
    const formattedNumber = num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${currencySymbol} ${formattedNumber}`;
  };

  const formatInteger = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Math.round(parseFloat(value));
    return new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const parseIBKRDate = (dateString) => {
    if (!dateString) return null;
    // IBKR date format: DD/MM/YY
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const year = parseInt(parts[2], 10);
      // Handle 2-digit years: assume 20xx for years < 50, 19xx for years >= 50
      const fullYear = year < 50 ? 2000 + year : 1900 + year;
      return new Date(fullYear, month, day);
    }
    return null;
  };

  /** IBKR date_time often "DD/MM/YY;HHMMSS" — combine with trade_date for sortable millis. */
  const parseTradeDateTimeMs = (trade) => {
    const base = parseIBKRDate(trade.trade_date);
    if (!base) return 0;
    let ms = base.getTime();
    const raw = trade.date_time || '';
    const semi = raw.indexOf(';');
    const timePart = semi >= 0 ? raw.slice(semi + 1).replace(/\D/g, '') : '';
    if (timePart.length >= 4) {
      const pad = timePart.padStart(6, '0').slice(-6);
      const hh = parseInt(pad.slice(0, 2), 10) || 0;
      const mm = parseInt(pad.slice(2, 4), 10) || 0;
      const ss = parseInt(pad.slice(4, 6), 10) || 0;
      ms += ((hh * 60 + mm) * 60 + ss) * 1000;
    }
    return ms;
  };

  const formatDateISO = (dateObj) => {
    if (!dateObj) return '';
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // ISO week helpers
  const getISOWeekYear = (dateObj) => {
    // Copy date, use UTC-ish logic to avoid DST issues
    const date = new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()));
    // Thursday in current week decides the year.
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    return { year: date.getUTCFullYear(), week: weekNo };
  };

  const getPeriodKeyForDate = (dateObj) => {
    if (!dateObj) return null;
    if (pnlPeriod === 'day') {
      return formatDateISO(dateObj);
    }
    if (pnlPeriod === 'week') {
      const { year, week } = getISOWeekYear(dateObj);
      return `${year}-W${String(week).padStart(2, '0')}`;
    }
    if (pnlPeriod === 'month') {
      return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
    }
    if (pnlPeriod === 'year') {
      return `${dateObj.getFullYear()}`;
    }
    return formatDateISO(dateObj);
  };

  const togglePnlGroupExpansion = (groupKey) => {
    const next = new Set(expandedPnlGroups);
    if (next.has(groupKey)) {
      next.delete(groupKey);
    } else {
      next.add(groupKey);
    }
    setExpandedPnlGroups(next);
  };

  const getDateRangeFilter = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateRange) {
      case 'last_7_days':
        const last7Days = new Date(today);
        last7Days.setDate(last7Days.getDate() - 7);
        return (date) => {
          const parsedDate = parseIBKRDate(date);
          return parsedDate && parsedDate >= last7Days;
        };
      
      case 'last_30_days':
        const last30Days = new Date(today);
        last30Days.setDate(last30Days.getDate() - 30);
        return (date) => {
          const parsedDate = parseIBKRDate(date);
          return parsedDate && parsedDate >= last30Days;
        };
      
      case 'past_month':
        // Previous complete month: from 1st to last day of previous month
        const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        return (date) => {
          const parsedDate = parseIBKRDate(date);
          if (!parsedDate) return false;
          // Set time to start of day for comparison
          const dateStart = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
          const monthStart = new Date(previousMonth.getFullYear(), previousMonth.getMonth(), previousMonth.getDate());
          const monthEnd = new Date(lastDayOfPreviousMonth.getFullYear(), lastDayOfPreviousMonth.getMonth(), lastDayOfPreviousMonth.getDate());
          return dateStart >= monthStart && dateStart <= monthEnd;
        };
      
      case 'this_month':
        // Current month: from 1st day of current month to today
        const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return (date) => {
          const parsedDate = parseIBKRDate(date);
          if (!parsedDate) return false;
          // Set time to start of day for comparison
          const dateStart = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
          const monthStart = new Date(currentMonthStart.getFullYear(), currentMonthStart.getMonth(), currentMonthStart.getDate());
          return dateStart >= monthStart && dateStart <= today;
        };
      
      case 'last_3_months':
        const last3Months = new Date(today);
        last3Months.setMonth(last3Months.getMonth() - 3);
        return (date) => {
          const parsedDate = parseIBKRDate(date);
          return parsedDate && parsedDate >= last3Months;
        };
      
      case 'last_1_year':
        const last1Year = new Date(today);
        last1Year.setFullYear(last1Year.getFullYear() - 1);
        return (date) => {
          const parsedDate = parseIBKRDate(date);
          return parsedDate && parsedDate >= last1Year;
        };
      
      case 'ytd':
        const yearStart = new Date(today.getFullYear(), 0, 1);
        return (date) => {
          const parsedDate = parseIBKRDate(date);
          return parsedDate && parsedDate >= yearStart;
        };
      
      case 'all':
      default:
        return () => true;
    }
  };

  // Helper function to determine option type (Long Call, Short Call, Long Put, Short Put)
  // IMPORTANT: Must consider open_close_indicator to correctly identify the original position type
  // Rules:
  // For OPENING trades:
  //   Long Call: put_call = "C" AND buy_sell = "BUY" AND quantity > 0
  //   Short Call: put_call = "C" AND buy_sell = "SELL" AND quantity < 0
  //   Long Put: put_call = "P" AND buy_sell = "BUY" AND quantity > 0
  //   Short Put: put_call = "P" AND buy_sell = "SELL" AND quantity < 0
  // Note: quantity is always an integer (never 0), so quantity = 1 or quantity = -1 checks are redundant
  // For CLOSING trades:
  //   Closing long call: SELL + negatieve quantity + indicator='C' → was LONG call
  //   Closing short call: BUY + positieve quantity + indicator='C' → was SHORT call
  //   Closing long put: SELL + negatieve quantity + indicator='C' → was LONG put
  //   Closing short put: BUY + positieve quantity + indicator='C' → was SHORT put
  const getOptionType = (trade) => {
    if (trade.asset_category !== 'OPT') return null;
    
    const putCall = (trade.put_call || '').toUpperCase();
    const quantity = parseFloat(trade.quantity || 0);
    const buySell = (trade.buy_sell || '').toUpperCase();
    const indicator = (trade.open_close_indicator || '').toUpperCase();
    const isClosing = indicator === 'C' || indicator === 'CLOSE';
    
    if (putCall === 'C') {
      if (isClosing) {
        // Closing trade: determine original position type
        // Closing long call: SELL + negatieve quantity + indicator='C' → was LONG call
        if (buySell === 'SELL' && quantity < 0) {
          return 'long_call';
        }
        // Closing short call: BUY + positieve quantity + indicator='C' → was SHORT call
        if (buySell === 'BUY' && quantity > 0) {
          return 'short_call';
        }
      } else {
        // Opening trade: use normal logic
        // Long Call: put_call = "C" AND buy_sell = "BUY" AND quantity > 0
        // Note: quantity is always an integer (never 0), so quantity = 1 is redundant
        if (buySell === 'BUY' && quantity > 0) {
          return 'long_call';
        }
        // Short Call: put_call = "C" AND buy_sell = "SELL" AND quantity < 0
        // Note: quantity is always an integer (never 0), so quantity = -1 is redundant
        if (buySell === 'SELL' && quantity < 0) {
          return 'short_call';
        }
      }
    } else if (putCall === 'P') {
      if (isClosing) {
        // Closing trade: determine original position type
        // Closing long put: SELL + negatieve quantity + indicator='C' → was LONG put
        if (buySell === 'SELL' && quantity < 0) {
          return 'long_put';
        }
        // Closing short put: BUY + positieve quantity + indicator='C' → was SHORT put
        if (buySell === 'BUY' && quantity > 0) {
          return 'short_put';
        }
      } else {
        // Opening trade: use normal logic
        // Long Put: put_call = "P" AND buy_sell = "BUY" AND quantity > 0
        // Note: quantity is always an integer (never 0), so quantity = 1 is redundant
        if (buySell === 'BUY' && quantity > 0) {
          return 'long_put';
        }
        // Short Put: put_call = "P" AND buy_sell = "SELL" AND quantity < 0
        // Note: quantity is always an integer (never 0), so quantity = -1 is redundant
        if (buySell === 'SELL' && quantity < 0) {
          return 'short_put';
        }
      }
    }
    
    return null;
  };

  // Helper function to determine if trade is opening or closing
  // Rules:
  // Opening long: quantity > 0 AND buy_sell = BUY AND open_close_indicator = O
  // Closing long: quantity < 0 AND buy_sell = SELL AND open_close_indicator = C
  // Opening short: quantity < 0 AND buy_sell = SELL AND open_close_indicator = O
  // Closing short: quantity > 0 AND buy_sell = BUY AND open_close_indicator = C
  const getTradeAction = (trade) => {
    const indicator = (trade.open_close_indicator || '').toUpperCase();
    const quantity = parseFloat(trade.quantity || 0);
    const buySell = (trade.buy_sell || '').toUpperCase();
    
    // Opening long: quantity > 0 AND buy_sell = BUY AND open_close_indicator = O
    if (quantity > 0 && buySell === 'BUY' && (indicator === 'O' || indicator === 'OPEN')) {
      return 'opening_long';
    }
    
    // Closing long: quantity < 0 AND buy_sell = SELL AND open_close_indicator = C
    if (quantity < 0 && buySell === 'SELL' && (indicator === 'C' || indicator === 'CLOSE')) {
      return 'closing_long';
    }
    
    // Opening short: quantity < 0 AND buy_sell = SELL AND open_close_indicator = O
    if (quantity < 0 && buySell === 'SELL' && (indicator === 'O' || indicator === 'OPEN')) {
      return 'opening_short';
    }
    
    // Closing short: quantity > 0 AND buy_sell = BUY AND open_close_indicator = C
    if (quantity > 0 && buySell === 'BUY' && (indicator === 'C' || indicator === 'CLOSE')) {
      return 'closing_short';
    }
    
    // Fallback: use open_close_indicator if available
    if (indicator === 'O' || indicator === 'OPEN') {
      return 'opening';
    }
    if (indicator === 'C' || indicator === 'CLOSE') {
      return 'closing';
    }
    
    return 'unknown';
  };

  const getFilteredTrades = (tradesList) => {
    let filtered = tradesList;
    
    // Apply trade filter (Open/Closed/Rolled/All) - using new getTradeAction logic
    if (tradeFilter !== 'All') {
      filtered = filtered.filter(trade => {
        if (tradeFilter === 'Rolled') {
          // Check if trade has "Rolled" tag
          const tags = trade.tags || [];
          return tags.some(tag => tag.name === 'Rolled');
        }
        const action = getTradeAction(trade);
        if (tradeFilter === 'Open') {
          return action === 'opening_long' || action === 'opening_short' || action === 'opening';
        } else if (tradeFilter === 'Closed') {
          return action === 'closing_long' || action === 'closing_short' || action === 'closing' || action === 'unknown';
        }
        return true;
      });
    }
    
    // Apply date range filter - skip if "all" is selected
    if (dateRange !== 'all') {
      const dateFilter = getDateRangeFilter();
      filtered = filtered.filter(trade => {
        return dateFilter(trade.trade_date);
      });
    }
    // If "all" is selected, don't filter by date at all
    
    // Apply symbol filter
    if (symbolFilter && symbolFilter.trim() !== '') {
      const filterValue = symbolFilter.trim().toUpperCase();
      filtered = filtered.filter(trade => {
        const underlyingSymbol = (trade.underlying_symbol || '').toUpperCase();
        const symbol = (trade.symbol || '').toUpperCase();
        return underlyingSymbol.includes(filterValue) || symbol.includes(filterValue);
      });
    }
    
    // Apply tag filter (case-insensitive partial match)
    if (tagFilter && tagFilter.trim() !== '') {
      const filterValue = tagFilter.trim().toUpperCase();
      filtered = filtered.filter(trade => {
        const tags = trade.tags || [];
        return tags.some(tag => {
          const tagName = (tag.name || '').toUpperCase();
          return tagName.includes(filterValue);
        });
      });
    }
    
    // Apply position filter (Long/Short) - using new getOptionType logic
    if (positionFilter !== 'All') {
      filtered = filtered.filter(trade => {
        // Only filter options
        if (trade.asset_category !== 'OPT') return true;
        
        const optionType = getOptionType(trade);
        if (positionFilter === 'Long') {
          return optionType === 'long_call' || optionType === 'long_put';
        } else if (positionFilter === 'Short') {
          return optionType === 'short_call' || optionType === 'short_put';
        }
        return true;
      });
    }
    
    // Apply option type filter (Call/Put) - using new getOptionType logic
    if (optionTypeFilter !== 'All') {
      filtered = filtered.filter(trade => {
        // Only filter options
        if (trade.asset_category !== 'OPT') return true;
        
        const optionType = getOptionType(trade);
        if (optionTypeFilter === 'Call') {
          return optionType === 'long_call' || optionType === 'short_call';
        } else if (optionTypeFilter === 'Put') {
          return optionType === 'long_put' || optionType === 'short_put';
        }
        return true;
      });
    }
    
    return filtered;
  };

  const getClosedConidGroups = () => {
    // Use the existing CONID logic (quantity-based) and select only truly closed groups.
    // Then, within those groups, keep only closing trades (indicator C/CLOSE) for P&L attribution.
    const groups = getGroupedTradesByConid();
    return groups
      .filter(g => g && g.isClosed && !g.errorMessage)
      .map(g => {
        const closingTrades = (g.trades || []).filter(t => {
          const action = getTradeAction(t);
          return action === 'closing_long' || action === 'closing_short' || action === 'closing';
        });

        // Fallback: if a broker export omits open_close_indicator, use all trades in the closed CONID group.
        // This keeps the page usable, but still prefers explicit close trades when available.
        const tradesForPnl = closingTrades.length > 0 ? closingTrades : (g.trades || []);

        // Determine "close date" as the newest trade date in the group.
        const closeDate = g.closeDate || null;
        const closeDateStr = g.closeDateStr || null;

        const totalPnl = tradesForPnl.reduce((sum, t) => sum + parseFloat(t.fifo_pnl_realized || 0), 0);

        return {
          ...g,
          tradesForPnl,
          closeDate,
          closeDateStr,
          totalPnlForPnlView: totalPnl,
        };
      });
  };

  const getFilteredClosedGroups = () => {
    // Reuse the existing filter logic, but applied to closing trade dates.
    const allClosed = getClosedConidGroups();

    // Account filter is already done server-side in fetchTrades via account_id param, but keep it safe.
    let groups = allClosed;

    // Date range filter: apply to the group's close date
    if (dateRange !== 'all') {
      const dateFilter = getDateRangeFilter();
      groups = groups.filter(g => dateFilter(g.closeDateStr));
    }

    // Symbol filter: apply to underlying symbol
    if (symbolFilter && symbolFilter.trim() !== '') {
      const filterValue = symbolFilter.trim().toUpperCase();
      groups = groups.filter(g => {
        const sym = (g.underlyingSymbol || '').toUpperCase();
        return sym.includes(filterValue);
      });
    }

    // Tag filter: apply to any trade in the group
    if (tagFilter && tagFilter.trim() !== '') {
      const filterValue = tagFilter.trim().toUpperCase();
      groups = groups.filter(g => {
        const tradesForConid = g.trades || [];
        return tradesForConid.some(trade => {
          const tags = trade.tags || [];
          return tags.some(tag => {
            const tagName = (tag.name || '').toUpperCase();
            return tagName.includes(filterValue);
          });
        });
      });
    }
    
    // Position filter (Long/Short): use new logic with getOptionType
    if (positionFilter !== 'All') {
      groups = groups.filter(g => {
        const tradesForConid = g.trades || [];
        // Find first opening trade if present, else first trade
        const opening = tradesForConid.find(t => {
          const action = getTradeAction(t);
          return action === 'opening_long' || action === 'opening_short' || action === 'opening';
        }) || tradesForConid[0];
        
        if (!opening || opening.asset_category !== 'OPT') return true;
        
        const optionType = getOptionType(opening);
        if (positionFilter === 'Long') {
          return optionType === 'long_call' || optionType === 'long_put';
        } else if (positionFilter === 'Short') {
          return optionType === 'short_call' || optionType === 'short_put';
        }
        return true;
      });
    }

    // Option type filter: use new logic with getOptionType
    if (optionTypeFilter !== 'All') {
      groups = groups.filter(g => {
        const tradesForConid = g.trades || [];
        const anyOptTrade = tradesForConid.find(t => t.asset_category === 'OPT') || null;
        // If not option, keep
        if (!anyOptTrade) return true;
        
        const optionType = getOptionType(anyOptTrade);
        if (optionTypeFilter === 'Call') {
          return optionType === 'long_call' || optionType === 'short_call';
        } else if (optionTypeFilter === 'Put') {
          return optionType === 'long_put' || optionType === 'short_put';
        }
        return true;
      });
    }

    return groups;
  };

  const getPnlGroups = () => {
    const closedGroups = getFilteredClosedGroups();
    const grouped = {};

    closedGroups.forEach(g => {
      const key = getPeriodKeyForDate(g.closeDate);
      if (!key) return;

      if (!grouped[key]) {
        grouped[key] = {
          key,
          totalPnl: 0,
          tradeCount: 0,
          conidCount: 0,
          items: []
        };
      }
      grouped[key].items.push(g);
      grouped[key].conidCount += 1;
      // Count individual trades shown when expanded (closing trades preferred)
      grouped[key].tradeCount += (g.tradesForPnl || []).length;
      grouped[key].totalPnl += (g.totalPnlForPnlView || 0);
    });

    // Sort by key descending (works for ISO date, YYYY-MM, YYYY and ISO week keys)
    return Object.values(grouped).sort((a, b) => (a.key < b.key ? 1 : a.key > b.key ? -1 : 0));
  };

  const getSortedTrades = (tradesList) => {
    const filtered = getFilteredTrades(tradesList);
    if (!sortConfig.key) return filtered;
    
    return [...filtered].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle nested properties
      if (sortConfig.key === 'symbol') {
        aVal = a.underlying_symbol || a.symbol || '';
        bVal = b.underlying_symbol || b.symbol || '';
      }
      
      // Handle numeric values
      if (['quantity', 'trade_price', 'trade_money', 'ib_commission', 'net_cash', 'cost', 'fifo_pnl_realized'].includes(sortConfig.key)) {
        aVal = parseFloat(aVal || 0);
        bVal = parseFloat(bVal || 0);
      }
      
      // Handle date comparison - parse DD/MM/YY format
      if (sortConfig.key === 'trade_date' || sortConfig.key === 'report_date' || sortConfig.key === 'orig_trade_date') {
        const parseDate = (dateStr) => {
          if (!dateStr || dateStr === '-') return 0;
          // Parse DD/MM/YY format
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
            const year = parseInt(parts[2], 10);
            // Handle 2-digit years: assume 20xx for years < 50, 19xx for years >= 50
            const fullYear = year < 50 ? 2000 + year : 1900 + year;
            return new Date(fullYear, month, day).getTime();
          }
          return 0;
        };
        aVal = parseDate(a[sortConfig.key]);
        bVal = parseDate(b[sortConfig.key]);
      } else if (typeof aVal === 'string') {
        // Handle string comparison
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const toggleSymbolExpansion = (symbol) => {
    const newExpanded = new Set(expandedSymbols);
    if (newExpanded.has(symbol)) {
      newExpanded.delete(symbol);
    } else {
      newExpanded.add(symbol);
    }
    setExpandedSymbols(newExpanded);
  };

  const toggleConidExpansion = (conid) => {
    const newExpanded = new Set(expandedConids);
    if (newExpanded.has(conid)) {
      newExpanded.delete(conid);
    } else {
      newExpanded.add(conid);
    }
    setExpandedConids(newExpanded);
  };

  // Group trades by symbol
  const getGroupedTrades = () => {
    const filtered = getFilteredTrades(trades);
    const grouped = {};
    
    filtered.forEach(trade => {
      const symbol = trade.underlying_symbol || trade.symbol || 'Unknown';
      if (!grouped[symbol]) {
        grouped[symbol] = {
          symbol: symbol,
          trades: [],
          totalPnl: 0,
          tradeCount: 0
        };
      }
      grouped[symbol].trades.push(trade);
      grouped[symbol].totalPnl += parseFloat(trade.fifo_pnl_realized || 0);
      grouped[symbol].tradeCount += 1;
    });
    
    return Object.values(grouped);
  };

  // Group trades by CONID
  const getGroupedTradesByConid = () => {
    const filtered = getFilteredTrades(trades);
    const grouped = {};
    
    filtered.forEach(trade => {
      const conid = trade.conid;
      // Skip trades without CONID
      if (!conid) return;
      
      if (!grouped[conid]) {
        grouped[conid] = {
          conid: conid,
          trades: [],
          totalPnl: 0,
          tradeCount: 0,
          openDate: null,
          closeDate: null,
          underlyingSymbol: null,
          isOpen: false,
          isClosed: false,
          openQuantity: 0,
          closeQuantity: 0,
          errorMessage: null
        };
      }
      grouped[conid].trades.push(trade);
      grouped[conid].totalPnl += parseFloat(trade.fifo_pnl_realized || 0);
      grouped[conid].tradeCount += 1;
      
      // Track underlying symbol (use first one found, they should all be the same for same CONID)
      if (!grouped[conid].underlyingSymbol) {
        grouped[conid].underlyingSymbol = trade.underlying_symbol || trade.symbol || null;
      }
      
      // Track quantities based on open/close indicator (not buy/sell)
      const indicator = (trade.open_close_indicator || '').toUpperCase();
      // Use absolute value of quantity to handle both positive and negative quantities
      const quantity = Math.abs(parseFloat(trade.quantity || 0));
      
      if (indicator === 'O' || indicator === 'OPEN') {
        grouped[conid].openQuantity += quantity;
      } else if (indicator === 'C' || indicator === 'CLOSE') {
        grouped[conid].closeQuantity += quantity;
      } else {
        // If indicator is missing or unknown, log error but don't count it
        console.warn(`Trade ${trade.trade_id} (CONID ${conid}) has unknown open_close_indicator: "${trade.open_close_indicator}"`);
      }
      
      // Track oldest (open date) and newest (close date) trade dates
      const tradeDate = parseIBKRDate(trade.trade_date);
      if (tradeDate) {
        if (!grouped[conid].openDate || tradeDate < grouped[conid].openDate) {
          grouped[conid].openDate = tradeDate;
          grouped[conid].openDateStr = trade.trade_date;
        }
        if (!grouped[conid].closeDate || tradeDate > grouped[conid].closeDate) {
          grouped[conid].closeDate = tradeDate;
          grouped[conid].closeDateStr = trade.trade_date;
        }
      }
    });
    
    // Determine if each CONID group is open or closed based on quantities
    // A CONID is closed if openQuantity == closeQuantity
    // A CONID is open if openQuantity > closeQuantity
    // Error if closeQuantity > openQuantity (more closed than opened)
    Object.values(grouped).forEach(group => {
      // Use a small epsilon for floating point comparison
      const epsilon = 0.0001;
      const diff = Math.abs(group.openQuantity - group.closeQuantity);
      
      if (group.closeQuantity > group.openQuantity + epsilon) {
        // Error: more closed than opened
        group.errorMessage = `Error: Closed quantity (${group.closeQuantity}) exceeds opened quantity (${group.openQuantity}) for CONID ${group.conid}`;
        group.isOpen = false;
        group.isClosed = false;
      } else if (diff < epsilon) {
        // All opened positions are closed (within floating point tolerance)
        group.isClosed = true;
        group.isOpen = false;
      } else {
        // Still have open positions
        group.isOpen = true;
        group.isClosed = false;
      }
    });

    Object.values(grouped).forEach((group) => {
      group.trades.sort((a, b) => parseTradeDateTimeMs(b) - parseTradeDateTimeMs(a));
    });

    return Object.values(grouped);
  };

  const getSortedGroupedTrades = () => {
    const grouped = getGroupedTrades();
    if (!sortConfig.key) return grouped;
    
    return [...grouped].sort((a, b) => {
      let aVal, bVal;
      
      if (sortConfig.key === 'symbol') {
        aVal = a.symbol.toLowerCase();
        bVal = b.symbol.toLowerCase();
      } else if (sortConfig.key === 'total_pnl') {
        aVal = a.totalPnl;
        bVal = b.totalPnl;
      } else if (sortConfig.key === 'trade_count') {
        aVal = a.tradeCount;
        bVal = b.tradeCount;
      } else {
        return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortedGroupedTradesByConid = () => {
    const grouped = getGroupedTradesByConid();
    if (!sortConfig.key) return grouped;
    
    return [...grouped].sort((a, b) => {
      let aVal, bVal;
      
      if (sortConfig.key === 'conid') {
        aVal = String(a.conid || '').toLowerCase();
        bVal = String(b.conid || '').toLowerCase();
      } else if (sortConfig.key === 'symbol') {
        aVal = (a.underlyingSymbol || '').toLowerCase();
        bVal = (b.underlyingSymbol || '').toLowerCase();
      } else if (sortConfig.key === 'total_pnl') {
        aVal = a.totalPnl;
        bVal = b.totalPnl;
      } else if (sortConfig.key === 'trade_count') {
        aVal = a.tradeCount;
        bVal = b.tradeCount;
      } else if (sortConfig.key === 'trade_date') {
        aVal = a.closeDate ? a.closeDate.getTime() : 0;
        bVal = b.closeDate ? b.closeDate.getTime() : 0;
      } else if (sortConfig.key === 'open_date') {
        aVal = a.openDate ? a.openDate.getTime() : 0;
        bVal = b.openDate ? b.openDate.getTime() : 0;
      } else if (sortConfig.key === 'close_date') {
        aVal = a.closeDate ? a.closeDate.getTime() : 0;
        bVal = b.closeDate ? b.closeDate.getTime() : 0;
      } else {
        return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredTrades = getSortedTrades(trades);
  const groupedTrades = getSortedGroupedTrades();
  const groupedTradesByConid = getSortedGroupedTradesByConid();
  const pnlGroups = getPnlGroups();

  // Calculate total P&L (realized for closed trades, unrealized for open trades)
  const calculateTotalPnl = () => {
    return filteredTrades.reduce((sum, trade) => {
      const isClosed = trade.open_close_indicator && 
        (trade.open_close_indicator.toUpperCase() === 'C' || trade.open_close_indicator.toUpperCase() === 'CLOSE');
      
      if (isClosed) {
        return sum + parseFloat(trade.fifo_pnl_realized || 0);
      } else {
        return sum + parseFloat(trade.mtm_pnl || 0);
      }
    }, 0);
  };

  // Calculate total P&L Realized for grouped trades
  const calculateGroupedTotalPnl = () => {
    return groupedTrades.reduce((sum, group) => {
      return sum + group.totalPnl;
    }, 0);
  };

  // Calculate total P&L Realized for CONID grouped trades
  const calculateConidGroupedTotalPnl = () => {
    return groupedTradesByConid.reduce((sum, group) => {
      return sum + group.totalPnl;
    }, 0);
  };

  const totalPnl = calculateTotalPnl();
  const groupedTotalPnl = calculateGroupedTotalPnl();
  const conidGroupedTotalPnl = calculateConidGroupedTotalPnl();
  const pnlTotal = pnlGroups.reduce((sum, g) => sum + (g.totalPnl || 0), 0);

  // Component for individual trade row with tag management
  const TradeRow = ({ trade, formatCurrency, formatInteger }) => {
    const [tagIds, setTagIds] = useState(trade.tags ? trade.tags.map(t => t.id) : []);
    const [currentTags, setCurrentTags] = useState(trade.tags || []);
    const [saving, setSaving] = useState(false);

    // Update local state when trade prop changes
    useEffect(() => {
      if (trade.tags) {
        setTagIds(trade.tags.map(t => t.id));
        setCurrentTags(trade.tags);
      }
    }, [trade.tags]);

    const handleTagChange = async (newTagIds) => {
      setTagIds(newTagIds);
      try {
        setSaving(true);
        const response = await axios.patch(`${API_URL}/portfolio/trades/${trade.id}/`, {
          tag_ids: newTagIds,
        });
        // Update the trade in the local state with the response data
        const updatedTrade = { ...trade, tags: response.data.tags || [] };
        setCurrentTags(updatedTrade.tags);
        setTrades(prev => prev.map(t => t.id === trade.id ? updatedTrade : t));
      } catch (err) {
        console.error('Error updating tags:', err);
        // Revert on error
        setTagIds(trade.tags ? trade.tags.map(t => t.id) : []);
        setCurrentTags(trade.tags || []);
      } finally {
        setSaving(false);
      }
    };

    return (
      <tr>
        <td className="account-cell">{trade.account_id}</td>
        <td className="date-cell">{trade.trade_date || '-'}</td>
        <td className="symbol-cell">{trade.underlying_symbol || trade.symbol || '-'}</td>
        <td className="conid-cell">{trade.conid || '-'}</td>
        <td className="description-cell" title={trade.description}>
          {trade.description || '-'}
        </td>
        <td className="buy-sell-cell">{trade.buy_sell || '-'}</td>
        <td className="open-close-cell">
          {trade.open_close_indicator ? (
            trade.open_close_indicator.toUpperCase() === 'O' || trade.open_close_indicator.toUpperCase() === 'OPEN' 
              ? 'Open' 
              : trade.open_close_indicator.toUpperCase() === 'C' || trade.open_close_indicator.toUpperCase() === 'CLOSE'
              ? 'Close'
              : trade.open_close_indicator
          ) : '-'}
        </td>
        <td className="number-cell">{formatInteger(trade.quantity)}</td>
        <td className="number-cell">{trade.trade_price ? parseFloat(trade.trade_price).toFixed(2) : '-'}</td>
        <td className="number-cell">{formatCurrency(trade.trade_money)}</td>
        <td className="number-cell">{formatCurrency(trade.ib_commission)}</td>
        <td className={(() => {
          // Check if trade is closed
          const isClosed = trade.open_close_indicator && 
            (trade.open_close_indicator.toUpperCase() === 'C' || trade.open_close_indicator.toUpperCase() === 'CLOSE');
          
          if (isClosed) {
            // For closed trades: use realized P&L with green/red
            const pnl = parseFloat(trade.fifo_pnl_realized || 0);
            return `number-cell ${pnl >= 0 ? 'positive' : 'negative'}`;
          } else {
            // For open trades: use mtm_pnl with orange
            return 'number-cell unrealized';
          }
        })()}>
          {(() => {
            // Check if trade is closed
            const isClosed = trade.open_close_indicator && 
              (trade.open_close_indicator.toUpperCase() === 'C' || trade.open_close_indicator.toUpperCase() === 'CLOSE');
            
            if (isClosed) {
              // For closed trades: show realized P&L
              return trade.fifo_pnl_realized ? formatCurrency(trade.fifo_pnl_realized) : '-';
            } else {
              // For open trades: show mtm_pnl (unrealized P&L)
              return trade.mtm_pnl ? formatCurrency(trade.mtm_pnl) : '-';
            }
          })()}
        </td>
        <td className="tags-cell">
          <TagSelector
            selectedTagIds={tagIds}
            onChange={handleTagChange}
            disabled={saving}
            initialTags={currentTags}
          />
        </td>
      </tr>
    );
  };

  return (
    <div className="trades-container">
      <div className="trades-header">
        <h2>Trades</h2>
        <p>All trades per account</p>
      </div>

      <div className="trades-content">
        <div className="trades-controls">
          {accounts.length > 0 && (
            <div className="account-selector">
              <label htmlFor="account-select">Account: </label>
              <select
                id="account-select"
                value={selectedAccount || 'all'}
                onChange={(e) => setSelectedAccount(e.target.value)}
              >
                <option value="all">All accounts</option>
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.display_name || account.broker_account_id}
                  </option>
                ))}
              </select>
            </div>
          )}
          {activeSubmenu !== 'pnl' && (
          <div className="trade-filter-selector">
            <label htmlFor="trade-filter-select">Trades: </label>
            <select
              id="trade-filter-select"
              value={tradeFilter}
              onChange={(e) => setTradeFilter(e.target.value)}
            >
              <option value="All">All</option>
              <option value="Open">Open</option>
              <option value="Closed">Closed</option>
              <option value="Rolled">Rolled</option>
            </select>
          </div>
          )}
          <div className="date-range-selector">
            <label htmlFor="date-range-select">Date Range: </label>
            <select
              id="date-range-select"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <option value="last_7_days">Last 7 days</option>
              <option value="last_30_days">Last 30 days</option>
              <option value="past_month">Past month</option>
              <option value="this_month">This Month</option>
              <option value="last_3_months">Last 3 months</option>
              <option value="last_1_year">Last 1 year</option>
              <option value="ytd">YTD</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="symbol-filter-selector">
            <label htmlFor="symbol-filter-input">Symbol: </label>
            <input
              id="symbol-filter-input"
              type="text"
              placeholder="Filter by symbol..."
              value={symbolFilter}
              onChange={(e) => setSymbolFilter(e.target.value)}
            />
          </div>
          <div className="tag-filter-selector">
            <label htmlFor="tag-filter-input">Tag: </label>
            <input
              id="tag-filter-input"
              type="text"
              placeholder="Filter by tag..."
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            />
          </div>
          <div className="position-filter-selector">
            <label htmlFor="position-filter-select">Position: </label>
            <select
              id="position-filter-select"
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
            >
              <option value="All">All</option>
              <option value="Long">Long</option>
              <option value="Short">Short</option>
            </select>
          </div>
          <div className="option-type-filter-selector">
            <label htmlFor="option-type-filter-select">Option Type: </label>
            <select
              id="option-type-filter-select"
              value={optionTypeFilter}
              onChange={(e) => setOptionTypeFilter(e.target.value)}
            >
              <option value="All">All</option>
              <option value="Call">Call</option>
              <option value="Put">Put</option>
            </select>
          </div>
          {activeSubmenu === 'pnl' ? (
            <div className="pnl-period-selector">
              <label htmlFor="pnl-period-select">Cumulate by: </label>
              <select
                id="pnl-period-select"
                value={pnlPeriod}
                onChange={(e) => setPnlPeriod(e.target.value)}
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </div>
          ) : (
          <div className="group-by-conid-selector">
            <label htmlFor="group-by-conid-checkbox">
              <input
                id="group-by-conid-checkbox"
                type="checkbox"
                checked={groupByConid}
                onChange={(e) => setGroupByConid(e.target.checked)}
              />
              Group by CONID
            </label>
          </div>
          )}
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : activeSubmenu === 'info' ? (
          <div className="info-section">
            <div className="info-content">
              <h3>Regels voor het bepalen van Option Types en Opening/Closing</h3>
              
              <div className="info-section-block">
                <h4>1. Bepalen van Option Type (Long Call, Short Call, Long Put, Short Put)</h4>
                <p className="info-intro">
                  Het type van een optie wordt bepaald op basis van de velden: <code>put_call</code>, <code>quantity</code>, <code>buy_sell</code>, en <strong>belangrijk:</strong> <code>open_close_indicator</code>.
                </p>
                
                <div className="info-subsection">
                  <h5>Voor OPENING trades (open_close_indicator = "O" of "OPEN"):</h5>
                  <p className="info-note" style={{marginBottom: '1rem'}}>
                    <strong>Let op:</strong> Quantity is altijd een geheel getal (positief of negatief) en kan <strong>nooit 0</strong> zijn.
                  </p>
                  <ul className="info-rules-list">
                    <li>
                      <strong>Long Call:</strong> <code>put_call = "C"</code> AND <code>buy_sell = "BUY"</code> AND <code>quantity &gt; 0</code>
                    </li>
                    <li>
                      <strong>Short Call:</strong> <code>put_call = "C"</code> AND <code>buy_sell = "SELL"</code> AND <code>quantity &lt; 0</code>
                    </li>
                    <li>
                      <strong>Long Put:</strong> <code>put_call = "P"</code> AND <code>buy_sell = "BUY"</code> AND <code>quantity &gt; 0</code>
                    </li>
                    <li>
                      <strong>Short Put:</strong> <code>put_call = "P"</code> AND <code>buy_sell = "SELL"</code> AND <code>quantity &lt; 0</code>
                    </li>
                  </ul>
                </div>
                
                <div className="info-subsection">
                  <h5>Voor CLOSING trades (open_close_indicator = "C" of "CLOSE"):</h5>
                  <p className="info-note">
                    <strong>Belangrijk:</strong> Bij closing trades wordt het <strong>originele position type</strong> bepaald dat wordt gesloten, niet het type van de closing trade zelf.
                  </p>
                  <div className="info-example-box">
                    <strong>Voorbeeld:</strong>
                    <ul style={{marginTop: '0.5rem', paddingLeft: '1.5rem'}}>
                      <li>Je opent een <strong>LONG CALL</strong> door een CALL optie te kopen (BUY, quantity &gt; 0)</li>
                      <li>Later sluit je deze positie door dezelfde CALL optie te verkopen (SELL, quantity &lt; 0, indicator="C")</li>
                      <li>De closing trade wordt geclassificeerd als <strong>"Long Call"</strong> (het originele type)</li>
                      <li>Niet als "Short Call", ook al is het technisch een SELL trade</li>
                    </ul>
                    <p style={{marginTop: '0.75rem', marginBottom: 0, fontStyle: 'italic', color: '#666'}}>
                      Dit is logisch omdat je een bestaande LONG positie sluit, niet een nieuwe SHORT positie opent.
                    </p>
                  </div>
                  <ul className="info-rules-list">
                    <li>
                      <strong>Closing Long Call:</strong> <code>put_call = "C"</code> AND <code>buy_sell = "SELL"</code> AND <code>quantity &lt; 0</code> AND <code>open_close_indicator = "C"</code><br />
                      <span className="info-explanation">→ Dit sluit een LONG call positie die je eerder hebt geopend</span>
                    </li>
                    <li>
                      <strong>Closing Short Call:</strong> <code>put_call = "C"</code> AND <code>buy_sell = "BUY"</code> AND <code>quantity &gt; 0</code> AND <code>open_close_indicator = "C"</code><br />
                      <span className="info-explanation">→ Dit sluit een SHORT call positie die je eerder hebt geopend (je koopt terug wat je verkocht had)</span>
                    </li>
                    <li>
                      <strong>Closing Long Put:</strong> <code>put_call = "P"</code> AND <code>buy_sell = "SELL"</code> AND <code>quantity &lt; 0</code> AND <code>open_close_indicator = "C"</code><br />
                      <span className="info-explanation">→ Dit sluit een LONG put positie die je eerder hebt geopend</span>
                    </li>
                    <li>
                      <strong>Closing Short Put:</strong> <code>put_call = "P"</code> AND <code>buy_sell = "BUY"</code> AND <code>quantity &gt; 0</code> AND <code>open_close_indicator = "C"</code><br />
                      <span className="info-explanation">→ Dit sluit een SHORT put positie die je eerder hebt geopend (je koopt terug wat je verkocht had)</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="info-section-block">
                <h4>2. Bepalen of een Trade Opening of Closing is</h4>
                <p className="info-intro">
                  Of een trade een positie opent of sluit wordt bepaald door de combinatie van <code>quantity</code>, <code>buy_sell</code>, en <code>open_close_indicator</code>.
                </p>
                
                <ul className="info-rules-list">
                  <li>
                    <strong>Opening Long:</strong> <code>quantity &gt; 0</code> AND <code>buy_sell = "BUY"</code> AND <code>open_close_indicator = "O"</code>
                  </li>
                  <li>
                    <strong>Closing Long:</strong> <code>quantity &lt; 0</code> AND <code>buy_sell = "SELL"</code> AND <code>open_close_indicator = "C"</code>
                  </li>
                  <li>
                    <strong>Opening Short:</strong> <code>quantity &lt; 0</code> AND <code>buy_sell = "SELL"</code> AND <code>open_close_indicator = "O"</code>
                  </li>
                  <li>
                    <strong>Closing Short:</strong> <code>quantity &gt; 0</code> AND <code>buy_sell = "BUY"</code> AND <code>open_close_indicator = "C"</code>
                  </li>
                </ul>
              </div>

              <div className="info-section-block">
                <h4>3. Belangrijke opmerkingen</h4>
                <ul className="info-notes-list">
                  <li>Het veld <code>open_close_indicator</code> wordt <strong>nooit genegeerd</strong> voor trades.</li>
                  <li>Voor closing trades wordt altijd het <strong>originele position type</strong> bepaald, niet het type van de closing trade zelf.</li>
                  <li>De logica werkt alleen voor opties (<code>asset_category = "OPT"</code>).</li>
                  <li>Alle velden worden case-insensitive verwerkt (hoofdletters/kleine letters).</li>
                </ul>
              </div>
            </div>
          </div>
        ) : activeSubmenu === 'pnl' ? (
          pnlGroups.length === 0 ? (
            <div className="empty-state">No closed trades found</div>
          ) : (
            <div className="trades-table-container">
              <table className="trades-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Closed Positions</th>
                    <th>Trades</th>
                    <th>Total P&amp;L</th>
                  </tr>
                </thead>
                <tbody>
                  {pnlGroups.map(group => {
                    const isExpanded = expandedPnlGroups.has(group.key);
                    return (
                      <React.Fragment key={group.key}>
                        <tr
                          className="group-header-row"
                          onClick={() => togglePnlGroupExpansion(group.key)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="date-cell">{isExpanded ? '▼' : '▶'} {group.key}</td>
                          <td className="number-cell">{group.conidCount}</td>
                          <td className="number-cell">{group.tradeCount}</td>
                          <td className={`number-cell ${group.totalPnl >= 0 ? 'positive' : 'negative'}`}>
                            {formatCurrency(group.totalPnl)}
                          </td>
                        </tr>

                        {isExpanded && (
                          <>
                            <tr className="group-detail-header-row">
                              <td className="group-detail-header-cell">Account</td>
                              <td className="group-detail-header-cell">Close Date</td>
                              <td className="group-detail-header-cell">Symbol</td>
                              <td className="group-detail-header-cell">Conid</td>
                              <td className="group-detail-header-cell">Description</td>
                              <td className="group-detail-header-cell">Buy/Sell</td>
                              <td className="group-detail-header-cell">Open/Close</td>
                              <td className="group-detail-header-cell">Quantity</td>
                              <td className="group-detail-header-cell">Price</td>
                              <td className="group-detail-header-cell">Trade Money</td>
                              <td className="group-detail-header-cell">Commission</td>
                              <td className="group-detail-header-cell">P&amp;L Realized</td>
                              <td className="group-detail-header-cell">Tags</td>
                            </tr>
                            {group.items.flatMap(item => (item.tradesForPnl || []).map((trade, idx) => (
                              <TradeRow
                                key={`${group.key}-${item.conid}-${trade.trade_id || idx}`}
                                trade={trade}
                                formatCurrency={formatCurrency}
                                formatInteger={formatInteger}
                              />
                            )))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <tr className="totals-row">
                    <td className="totals-cell" colSpan="3">
                      <strong>Total</strong>
                    </td>
                    <td className={`totals-cell number-cell ${pnlTotal >= 0 ? 'positive' : 'negative'}`}>
                      <strong>{formatCurrency(pnlTotal)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )
        ) : activeSubmenu === 'group-on-symbol' ? (
          groupedTrades.length === 0 ? (
            <div className="empty-state">No trades found</div>
          ) : (
            <div className="trades-table-container">
              <table className="trades-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('symbol')} className="sortable-header">
                      Symbol {getSortIcon('symbol')}
                    </th>
                    <th onClick={() => handleSort('trade_count')} className="sortable-header">
                      Trade Count {getSortIcon('trade_count')}
                    </th>
                    <th onClick={() => handleSort('total_pnl')} className="sortable-header">
                      Total P&L {getSortIcon('total_pnl')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groupedTrades.map((group, index) => {
                    const isExpanded = expandedSymbols.has(group.symbol);
                    return (
                      <React.Fragment key={group.symbol || index}>
                        <tr 
                          className="group-header-row"
                          onClick={() => toggleSymbolExpansion(group.symbol)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="symbol-cell">
                            {isExpanded ? '▼' : '▶'} {group.symbol}
                          </td>
                          <td className="number-cell">{group.tradeCount}</td>
                          <td className={`number-cell ${group.totalPnl >= 0 ? 'positive' : 'negative'}`}>
                            {formatCurrency(group.totalPnl)}
                          </td>
                        </tr>
                        {isExpanded && (
                          <>
                            <tr className="group-detail-header-row">
                              <td className="group-detail-header-cell">Account</td>
                              <td className="group-detail-header-cell">Open Date</td>
                              <td className="group-detail-header-cell">Symbol</td>
                              <td className="group-detail-header-cell">Conid</td>
                              <td className="group-detail-header-cell">Description</td>
                              <td className="group-detail-header-cell">Buy/Sell</td>
                              <td className="group-detail-header-cell">Open/Close</td>
                              <td className="group-detail-header-cell">Quantity</td>
                              <td className="group-detail-header-cell">Price</td>
                              <td className="group-detail-header-cell">Trade Money</td>
                              <td className="group-detail-header-cell">Commission</td>
                              <td className="group-detail-header-cell">P&L Realized</td>
                              <td className="group-detail-header-cell">Tags</td>
                            </tr>
                            {group.trades.map((trade, tradeIndex) => (
                              <TradeRow
                                key={trade.trade_id || tradeIndex}
                                trade={trade}
                                formatCurrency={formatCurrency}
                                formatInteger={formatInteger}
                              />
                            ))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <tr className="totals-row">
                    <td className="totals-cell" colSpan="2">
                      <strong>Total</strong>
                    </td>
                    <td className={`totals-cell number-cell ${groupedTotalPnl >= 0 ? 'positive' : 'negative'}`}>
                      <strong>{formatCurrency(groupedTotalPnl)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )
        ) : groupByConid ? (
          groupedTradesByConid.length === 0 ? (
            <div className="empty-state">No trades found</div>
          ) : (
            <div className="trades-table-container">
              <table className="trades-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('conid')} className="sortable-header">
                      CONID {getSortIcon('conid')}
                    </th>
                    <th onClick={() => handleSort('symbol')} className="sortable-header">
                      Underlying Symbol {getSortIcon('symbol')}
                    </th>
                    <th onClick={() => handleSort('open_date')} className="sortable-header">
                      Open Date {getSortIcon('open_date')}
                    </th>
                    <th onClick={() => handleSort('close_date')} className="sortable-header">
                      Close Date {getSortIcon('close_date')}
                    </th>
                    <th onClick={() => handleSort('trade_count')} className="sortable-header">
                      Trade Count {getSortIcon('trade_count')}
                    </th>
                    <th>Open/Close</th>
                    <th onClick={() => handleSort('total_pnl')} className="sortable-header">
                      Total P&L {getSortIcon('total_pnl')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groupedTradesByConid.map((group, index) => {
                    const isExpanded = expandedConids.has(group.conid);
                    
                    return (
                      <React.Fragment key={group.conid || index}>
                        <tr 
                          className="group-header-row"
                          onClick={() => toggleConidExpansion(group.conid)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="conid-cell">
                            {isExpanded ? '▼' : '▶'} {group.conid}
                          </td>
                          <td className="symbol-cell">{group.underlyingSymbol || '-'}</td>
                          <td className="date-cell">{group.openDateStr || '-'}</td>
                          <td className="date-cell">{group.closeDateStr || '-'}</td>
                          <td className="number-cell">{group.tradeCount}</td>
                          <td className="open-close-cell">
                            {group.errorMessage ? (
                              <span 
                                className="status-error" 
                                title={group.errorMessage}
                                style={{ 
                                  color: '#dc3545', 
                                  cursor: 'help',
                                  textDecoration: 'underline',
                                  textDecorationStyle: 'dotted'
                                }}
                              >
                                Error
                              </span>
                            ) : group.isOpen ? (
                              <span className="status-open">Open</span>
                            ) : (
                              <span className="status-closed">Closed</span>
                            )}
                          </td>
                          <td className={`number-cell ${group.totalPnl >= 0 ? 'positive' : 'negative'}`}>
                            {formatCurrency(group.totalPnl)}
                          </td>
                        </tr>
                        {isExpanded && (
                          <>
                            <tr className="group-detail-header-row">
                              <td className="group-detail-header-cell">Account</td>
                              <td className="group-detail-header-cell">Open Date</td>
                              <td className="group-detail-header-cell">Symbol</td>
                              <td className="group-detail-header-cell">Conid</td>
                              <td className="group-detail-header-cell">Description</td>
                              <td className="group-detail-header-cell">Buy/Sell</td>
                              <td className="group-detail-header-cell">Open/Close</td>
                              <td className="group-detail-header-cell">Quantity</td>
                              <td className="group-detail-header-cell">Price</td>
                              <td className="group-detail-header-cell">Trade Money</td>
                              <td className="group-detail-header-cell">Commission</td>
                              <td className="group-detail-header-cell">P&L Realized</td>
                              <td className="group-detail-header-cell">Manage Tags</td>
                            </tr>
                            {group.trades.map((trade, tradeIndex) => (
                              <TradeRow
                                key={trade.trade_id || tradeIndex}
                                trade={trade}
                                formatCurrency={formatCurrency}
                                formatInteger={formatInteger}
                              />
                            ))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <tr className="totals-row">
                    <td className="totals-cell" colSpan="6">
                      <strong>Total</strong>
                    </td>
                    <td className={`totals-cell number-cell ${conidGroupedTotalPnl >= 0 ? 'positive' : 'negative'}`}>
                      <strong>{formatCurrency(conidGroupedTotalPnl)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )
        ) : filteredTrades.length === 0 ? (
          <div className="empty-state">No trades found</div>
        ) : (
          <div className="trades-table-container">
            <div style={{ 
              fontSize: '0.85rem', 
              color: '#666', 
              marginBottom: '10px', 
              padding: '8px 12px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '4px',
              borderLeft: '3px solid #ff8c00'
            }}>
              <span style={{ fontWeight: '500' }}>P&L Colors: </span>
              <span style={{ color: '#ff8c00' }}>Orange</span> = Unrealized P&L (open positions), 
              <span style={{ color: '#28a745' }}> Green</span>/<span style={{ color: '#dc3545' }}>Red</span> = Realized P&L (closed positions)
            </div>
            <table className="trades-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('account_id')} className="sortable-header">
                    Account {getSortIcon('account_id')}
                  </th>
                  <th onClick={() => handleSort('trade_date')} className="sortable-header">
                    Date {getSortIcon('trade_date')}
                  </th>
                  <th onClick={() => handleSort('symbol')} className="sortable-header">
                    Symbol {getSortIcon('symbol')}
                  </th>
                  <th onClick={() => handleSort('conid')} className="sortable-header">
                    Conid {getSortIcon('conid')}
                  </th>
                  <th onClick={() => handleSort('description')} className="sortable-header">
                    Description {getSortIcon('description')}
                  </th>
                  <th onClick={() => handleSort('buy_sell')} className="sortable-header">
                    Buy/Sell {getSortIcon('buy_sell')}
                  </th>
                  <th onClick={() => handleSort('open_close_indicator')} className="sortable-header">
                    Open/Close {getSortIcon('open_close_indicator')}
                  </th>
                  <th onClick={() => handleSort('quantity')} className="sortable-header">
                    Quantity {getSortIcon('quantity')}
                  </th>
                  <th onClick={() => handleSort('trade_price')} className="sortable-header">
                    Price {getSortIcon('trade_price')}
                  </th>
                  <th onClick={() => handleSort('trade_money')} className="sortable-header">
                    Trade Money {getSortIcon('trade_money')}
                  </th>
                  <th onClick={() => handleSort('ib_commission')} className="sortable-header">
                    Commission {getSortIcon('ib_commission')}
                  </th>
                  <th onClick={() => handleSort('fifo_pnl_realized')} className="sortable-header">
                    P&L {getSortIcon('fifo_pnl_realized')}
                    <span 
                      className="info-icon" 
                      title="Orange = Unrealized P&L (open positions). Green/Red = Realized P&L (closed positions)"
                      style={{ 
                        marginLeft: '5px',
                        fontSize: '12px',
                        color: '#666',
                        cursor: 'help'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      ℹ️
                    </span>
                  </th>
                  <th>Manage Tags</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrades.map((trade, index) => (
                  <TradeRow 
                    key={trade.trade_id || index}
                    trade={trade}
                    formatCurrency={formatCurrency}
                    formatInteger={formatInteger}
                  />
                ))}
                <tr className="totals-row">
                  <td className="totals-cell" colSpan="12">
                    <strong>Total</strong>
                  </td>
                  <td className={(() => {
                    // Check if we have any open trades with unrealized P&L
                    const hasUnrealized = filteredTrades.some(trade => {
                      const isClosed = trade.open_close_indicator && 
                        (trade.open_close_indicator.toUpperCase() === 'C' || trade.open_close_indicator.toUpperCase() === 'CLOSE');
                      return !isClosed && trade.mtm_pnl;
                    });
                    
                    if (hasUnrealized) {
                      return 'totals-cell number-cell unrealized';
                    } else {
                      return `totals-cell number-cell ${totalPnl >= 0 ? 'positive' : 'negative'}`;
                    }
                  })()}>
                    <strong>{formatCurrency(totalPnl)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Trades;

